import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

const MarketMultiSelect = ({
  marketData,
  isLoadingLeagueData,
  Messages,
  setMastermarketid,
  resetTrigger,
  subscriptionStatus,
  showPopupSignup
}) => {
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const allowedMarkets = ["1X2", "1st Half Result", "1st Half Goals"];

  // Memoize filtered market data
  const filteredMarketData = useMemo(() => {
    if (!searchTerm) return marketData;
    const filtered = marketData.filter((item) =>
      item.marketname.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return filtered.length > 0 ? filtered : [];
  }, [marketData, searchTerm]);

  const toggleMarket = useCallback(
    (marketId) => {
      setSelectedMarkets((prevSelected) => {
        const updatedSelection = prevSelected.includes(marketId)
          ? prevSelected.filter((id) => id !== marketId)
          : [...prevSelected, marketId];
        setMastermarketid(updatedSelection);
        return updatedSelection;
      });
    },
    [setMastermarketid]
  );

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (resetTrigger) {
      setSelectedMarkets([]);
      setMastermarketid([]);
    }
  }, [resetTrigger, setMastermarketid]);

  // Select/Deselect All
  const toggleSelectAll = useCallback(() => {
    const unlockedMarkets = filteredMarketData.filter(
      (item) =>
        !((subscriptionStatus === "Free" || subscriptionStatus === null || subscriptionStatus === "Pending") && !allowedMarkets.includes(item.marketname))
    );

    if (selectedMarkets.length === unlockedMarkets.length) {
      setSelectedMarkets([]);
      setMastermarketid([]);
    } else {
      const allMarketIds = unlockedMarkets.map((item) => item.mastermarketid);
      setSelectedMarkets(allMarketIds);
      setMastermarketid(allMarketIds);
    }
  }, [filteredMarketData, selectedMarkets.length, setMastermarketid, subscriptionStatus]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
        if (showPopupSignup) {
          setIsOpen(false);
        }
      }, [showPopupSignup]);

  return (
    <div className="dropdown-container" ref={dropdownRef}>
      <div className="dropdown-header dropdown-text" onClick={toggleDropdown}>
        {selectedMarkets.length > 0
          ? (() => {
              const marketNames = selectedMarkets
                .map(
                  (id) =>
                    marketData.find((item) => item.mastermarketid === id)
                      ?.marketname
                )
                .filter(Boolean);
              return marketNames.length > 2
                ? `${marketNames.slice(0, 2).join(", ")}...`
                : marketNames.join(", ");
            })()
          : "Choose Market"}
        <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}></span>
      </div>

      {isOpen && (
        <div className="dropdown-list">
          {/* Search Box */}
          <div className="AF-formControl_tribet">
            <input
              type="text"
              placeholder="Search Markets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              id="input_treble_search_market"
            />
          </div>

          {/* Select / Deselect All Button */}
          {!isLoadingLeagueData && filteredMarketData.length > 0 && (
            <div className="AF-formControl_multibet">
              <input
                type="checkbox"
                className="form-check-input"
                id="input_treble_checkbox_market"
                checked={
                  selectedMarkets.length ===
                  filteredMarketData.filter(
                    (item) =>
                      !((subscriptionStatus === "Free" || subscriptionStatus === null) &&
                        !allowedMarkets.includes(item.marketname))
                  ).length
                }
                onChange={toggleSelectAll}
              />
              <label className="label" htmlFor="input_treble_checkbox_market">
                Select / Deselect All
              </label>
            </div>
          )}

          {/* Market List */}
          {!isLoadingLeagueData ? (
            filteredMarketData.length > 0 ? (
              filteredMarketData.map((item) => {
                const isLocked =
                  (subscriptionStatus === "Free" || subscriptionStatus === null) &&
                  !allowedMarkets.includes(item.marketname);

                return (
                  <div
                    key={item.mastermarketid}
                    className={`AF-formControl_multibet ${
                      isLocked ? "locked" : ""
                    }`}
                    title={isLocked ? "Go Pro to unlock all leagues and markets." :""}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`treble_market-${item.mastermarketid}`}
                      value={item.mastermarketid}
                      checked={selectedMarkets.includes(item.mastermarketid)}
                      onChange={() => toggleMarket(item.mastermarketid)}
                      disabled={isLocked}
                    />
                    <label
                      className="label"
                      htmlFor={`treble_market-${item.mastermarketid}`}
                    >
                      {item.marketname}
                    </label>
                    {isLocked && <span className="lock-icon">🔒</span>}
                  </div>
                );
              })
            ) : (
              <div className="search_error-text">No matching found.</div>
            )
          ) : (
            <div className="search_error-text">{Messages.apiLoading}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MarketMultiSelect;
